import React, { useState } from 'react';
import { FileCode, Loader2, ArrowRight, AlertCircle, EyeIcon, EyeOffIcon, KeyIcon, MailIcon, LockIcon, ArrowLeftIcon, ShieldCheckIcon, PhoneIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
type LoginStep = 'email' | 'password' | 'set-password' | 'forgot' | 'reset-code' | 'quick-pin' | 'not-found';
export function Login() {
  const {
    identifyUser,
    verifyUserPassword,
    setupUserPassword,
    verifyQuickPin,
    requestPasswordReset,
    resetPasswordWithToken,
    loginState,
    getAdminContacts
  } = useWorkspace();
  const [step, setStep] = useState<LoginStep>(loginState?.needsPin ? 'quick-pin' : 'email');
  const [email, setEmail] = useState(loginState?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pin, setPin] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetRequested, setResetRequested] = useState(false);
  const [adminContacts, setAdminContacts] = useState<{
    name: string;
    email: string;
    phone: string;
    role: string;
  }[]>([]);
  // Step 1: Check if email exists
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await identifyUser(email);
      if (result === 'not-found') {
        setAdminContacts(getAdminContacts());
        setStep('not-found');
      } else if (result === 'suspended') {
        setError('Your account has been suspended. Contact your administrator.');
      } else if (result === 'needs-password') {
        setStep('set-password');
      } else if (result === 'has-password') {
        setStep('password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Step 2a: Verify password
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await verifyUserPassword(email, password);
      if (!success) {
        setError('Incorrect password. Please try again.');
      }
      // If success, context will set appState to 'ready'
    } catch (err) {
      console.error('Password verification error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Step 2b: Set initial password
  const handleSetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !confirmPassword) return;
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await setupUserPassword(email, password);
      if (success) {
        toast.success('Password created successfully! You are now logged in.');
      } else {
        setError('Failed to set password. Please try again.');
      }
    } catch (err) {
      console.error('Set password error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Quick PIN verification
  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await verifyQuickPin(pin);
      if (!success) {
        setError('Incorrect PIN. Please try again.');
      }
    } catch (err) {
      console.error('PIN verification error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Forgot password — request reset
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await requestPasswordReset(resetEmail);
      if (success) {
        setResetRequested(true);
        toast.success('Reset code generated. Contact your administrator for the code.');
      } else {
        setError('No account found with this email.');
      }
    } catch (err) {
      console.error('Reset request error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Reset password with code
  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode || !newPassword || !confirmNewPassword) return;
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await resetPasswordWithToken(resetEmail, resetCode, newPassword);
      if (success) {
        toast.success('Password reset successfully! Please log in.');
        setStep('email');
        setEmail(resetEmail);
        setPassword('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setError('Invalid or expired reset code. Please try again or request a new code.');
      }
    } catch (err) {
      console.error('Reset error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderError = () => error ? <div className="rounded-lg bg-red-50 p-3 flex items-start gap-2" data-id="element-4759">
        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-4760" />
        <p className="text-sm text-red-600" data-id="element-4761">{error}</p>
      </div> : null;
  const inputClass = 'block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-4 py-2.5 bg-white';
  const btnClass = 'w-full flex items-center justify-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-4762">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8" data-id="element-4763">
        {/* Quick PIN Screen */}
        {step === 'quick-pin' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4764">
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4765">
                <ShieldCheckIcon className="h-6 w-6 text-blue-600" data-id="element-4766" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4767">
                Welcome back
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4768">
                Enter your Quick PIN to continue as{' '}
                <span className="font-medium text-gray-700" data-id="element-4769">
                  {loginState?.email}
                </span>
              </p>
            </div>
            <form onSubmit={handlePinSubmit} className="space-y-4" data-id="element-4770">
              <div data-id="element-4771">
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4772">
                  Quick PIN
                </label>
                <input type="password" id="pin" required className={inputClass} placeholder="Enter your PIN or phrase" value={pin} onChange={e => {
              setPin(e.target.value);
              setError(null);
            }} autoFocus data-id="element-4773" />
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !pin} className={btnClass} data-id="element-4774">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4775" /> : <>
                    Unlock <ArrowRight className="h-4 w-4" data-id="element-4776" />
                  </>}
              </button>
              <div className="flex items-center justify-between text-xs" data-id="element-4777">
                <button type="button" onClick={() => {
              setStep('password');
              setError(null);
            }} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-4778">
                  Use password instead
                </button>
                <button type="button" onClick={() => {
              setStep('email');
              setEmail('');
              setError(null);
            }} className="text-gray-500 hover:text-gray-700" data-id="element-4779">
                  Switch account
                </button>
              </div>
            </form>
          </>}

        {/* Email Screen */}
        {step === 'email' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4780">
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4781">
                <FileCode className="h-6 w-6 text-blue-600" data-id="element-4782" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4783">Welcome</h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4784">
                Enter your email to access ConfigConsole.
              </p>
            </div>
            <form onSubmit={handleEmailSubmit} className="space-y-4" data-id="element-4785">
              <div data-id="element-4786">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4787">
                  Email Address
                </label>
                <div className="relative" data-id="element-4788">
                  <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4789" />
                  <input type="email" id="email" required className={`${inputClass} pl-10`} placeholder="you@company.com" value={email} onChange={e => {
                setEmail(e.target.value);
                setError(null);
              }} autoFocus data-id="element-4790" />
                </div>
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !email.trim()} className={btnClass} data-id="element-4791">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4792" /> : <>
                    Continue <ArrowRight className="h-4 w-4" data-id="element-4793" />
                  </>}
              </button>
            </form>
          </>}

        {/* Password Screen */}
        {step === 'password' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4794">
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4795">
                <LockIcon className="h-6 w-6 text-blue-600" data-id="element-4796" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4797">
                Enter password
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4798">
                Logging in as{' '}
                <span className="font-medium text-gray-700" data-id="element-4799">{email}</span>
              </p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4" data-id="element-4800">
              <div data-id="element-4801">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4802">
                  Password
                </label>
                <div className="relative" data-id="element-4803">
                  <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4804" />
                  <input type={showPassword ? 'text' : 'password'} id="password" required className={`${inputClass} pl-10 pr-10`} placeholder="Enter your password" value={password} onChange={e => {
                setPassword(e.target.value);
                setError(null);
              }} autoFocus data-id="element-4805" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4806">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-4807" /> : <EyeIcon className="h-4 w-4" data-id="element-4808" />}
                  </button>
                </div>
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !password} className={btnClass} data-id="element-4809">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4810" /> : <>
                    Log In <ArrowRight className="h-4 w-4" data-id="element-4811" />
                  </>}
              </button>
              <div className="flex items-center justify-between text-xs" data-id="element-4812">
                <button type="button" onClick={() => {
              setStep('forgot');
              setResetEmail(email);
              setError(null);
            }} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-4813">
                  Forgot password?
                </button>
                <button type="button" onClick={() => {
              setStep('email');
              setPassword('');
              setError(null);
            }} className="text-gray-500 hover:text-gray-700" data-id="element-4814">
                  <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4815" />
                  Back
                </button>
              </div>
            </form>
          </>}

        {/* Set Password Screen (first time) */}
        {step === 'set-password' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4816">
              <div className="h-12 w-12 bg-green-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4817">
                <KeyIcon className="h-6 w-6 text-green-600" data-id="element-4818" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4819">
                Create your password
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4820">
                Welcome! Set a password for{' '}
                <span className="font-medium text-gray-700" data-id="element-4821">{email}</span> to
                secure your account.
              </p>
            </div>
            <form onSubmit={handleSetPasswordSubmit} className="space-y-4" data-id="element-4822">
              <div data-id="element-4823">
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4824">
                  Password
                </label>
                <div className="relative" data-id="element-4825">
                  <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4826" />
                  <input type={showPassword ? 'text' : 'password'} id="new-password" required minLength={8} className={`${inputClass} pl-10 pr-10`} placeholder="At least 8 characters" value={password} onChange={e => {
                setPassword(e.target.value);
                setError(null);
              }} autoFocus data-id="element-4827" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4828">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-4829" /> : <EyeIcon className="h-4 w-4" data-id="element-4830" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1" data-id="element-4831">
                  Minimum 8 characters
                </p>
              </div>
              <div data-id="element-4832">
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4833">
                  Confirm Password
                </label>
                <div className="relative" data-id="element-4834">
                  <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4835" />
                  <input type={showConfirmPassword ? 'text' : 'password'} id="confirm-password" required minLength={8} className={`${inputClass} pl-10 pr-10`} placeholder="Re-enter your password" value={confirmPassword} onChange={e => {
                setConfirmPassword(e.target.value);
                setError(null);
              }} data-id="element-4836" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4837">
                    {showConfirmPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-4838" /> : <EyeIcon className="h-4 w-4" data-id="element-4839" />}
                  </button>
                </div>
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !password || !confirmPassword} className={btnClass} data-id="element-4840">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4841" /> : <>
                    Create Password & Log In <ArrowRight className="h-4 w-4" data-id="element-4842" />
                  </>}
              </button>
              <button type="button" onClick={() => {
            setStep('email');
            setPassword('');
            setConfirmPassword('');
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4843">
                <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4844" />
                Back to email
              </button>
            </form>
          </>}

        {/* Forgot Password Screen */}
        {step === 'forgot' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4845">
              <div className="h-12 w-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4846">
                <KeyIcon className="h-6 w-6 text-amber-600" data-id="element-4847" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4848">
                Reset password
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4849">
                Enter your email to receive a reset code from your
                administrator.
              </p>
            </div>
            {!resetRequested ? <form onSubmit={handleForgotSubmit} className="space-y-4" data-id="element-4850">
                <div data-id="element-4851">
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4852">
                    Email Address
                  </label>
                  <input type="email" id="reset-email" required className={inputClass} placeholder="you@company.com" value={resetEmail} onChange={e => {
              setResetEmail(e.target.value);
              setError(null);
            }} autoFocus data-id="element-4853" />
                </div>
                {renderError()}
                <button type="submit" disabled={isSubmitting || !resetEmail.trim()} className={btnClass} data-id="element-4854">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4855" /> : <>
                      Request Reset Code <ArrowRight className="h-4 w-4" data-id="element-4856" />
                    </>}
                </button>
                <button type="button" onClick={() => {
            setStep('email');
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4857">
                  <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4858" />
                  Back to login
                </button>
              </form> : <div className="space-y-4" data-id="element-4859">
                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800" data-id="element-4860">
                  <p className="font-medium mb-1" data-id="element-4861">Reset code generated</p>
                  <p data-id="element-4862">
                    Contact your team administrator to get your reset code. They
                    can find it in the Users management page.
                  </p>
                </div>
                <button type="button" onClick={() => setStep('reset-code')} className={btnClass} data-id="element-4863">
                  I have my code <ArrowRight className="h-4 w-4" data-id="element-4864" />
                </button>
                <button type="button" onClick={() => {
            setStep('email');
            setResetRequested(false);
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4865">
                  <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4866" />
                  Back to login
                </button>
              </div>}
          </>}

        {/* Reset Code Screen */}
        {step === 'reset-code' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4867">
              <div className="h-12 w-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4868">
                <KeyIcon className="h-6 w-6 text-amber-600" data-id="element-4869" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4870">
                Enter reset code
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4871">
                Enter the code from your administrator and set a new password.
              </p>
            </div>
            <form onSubmit={handleResetSubmit} className="space-y-4" data-id="element-4872">
              <div data-id="element-4873">
                <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4874">
                  Reset Code
                </label>
                <input type="text" id="reset-code" required className={`${inputClass} font-mono tracking-wider`} placeholder="Enter code" value={resetCode} onChange={e => {
              setResetCode(e.target.value);
              setError(null);
            }} autoFocus data-id="element-4875" />
              </div>
              <div data-id="element-4876">
                <label htmlFor="new-pw" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4877">
                  New Password
                </label>
                <input type="password" id="new-pw" required minLength={8} className={inputClass} placeholder="At least 8 characters" value={newPassword} onChange={e => {
              setNewPassword(e.target.value);
              setError(null);
            }} data-id="element-4878" />
              </div>
              <div data-id="element-4879">
                <label htmlFor="confirm-new-pw" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4880">
                  Confirm New Password
                </label>
                <input type="password" id="confirm-new-pw" required minLength={8} className={inputClass} placeholder="Re-enter new password" value={confirmNewPassword} onChange={e => {
              setConfirmNewPassword(e.target.value);
              setError(null);
            }} data-id="element-4881" />
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !resetCode || !newPassword || !confirmNewPassword} className={btnClass} data-id="element-4882">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4883" /> : <>
                    Reset Password <ArrowRight className="h-4 w-4" data-id="element-4884" />
                  </>}
              </button>
              <button type="button" onClick={() => {
            setStep('forgot');
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4885">
                <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4886" />
                Back
              </button>
            </form>
          </>}

        {/* Not Found Screen */}
        {step === 'not-found' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4887">
              <div className="h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4888">
                <AlertCircle className="h-6 w-6 text-red-600" data-id="element-4889" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4890">
                Account not found
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4891">
                We couldn't find an account for{' '}
                <span className="font-medium text-gray-700" data-id="element-4892">{email}</span>.
              </p>
            </div>

            <div className="space-y-6" data-id="element-4893">
              <div data-id="element-4894">
                <h2 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4895">
                  Contact an administrator to get access:
                </h2>
                {adminContacts.length > 0 ? <div className="space-y-3 max-h-60 overflow-y-auto pr-2" data-id="element-4896">
                    {adminContacts.map((admin, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-4897">
                        <div className="flex justify-between items-start mb-2" data-id="element-4898">
                          <span className="font-medium text-sm text-gray-900" data-id="element-4899">
                            {admin.name}
                          </span>
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 uppercase tracking-wider" data-id="element-4900">
                            {admin.role}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 space-y-1.5" data-id="element-4901">
                          <div className="flex items-center gap-2" data-id="element-4902">
                            <MailIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-4903" />
                            <a href={`mailto:${admin.email}`} className="hover:text-blue-600 transition-colors truncate" data-id="element-4904">
                              {admin.email}
                            </a>
                          </div>
                          {admin.phone && <div className="flex items-center gap-2" data-id="element-4905">
                              <PhoneIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-4906" />
                              <a href={`tel:${admin.phone}`} className="hover:text-blue-600 transition-colors truncate" data-id="element-4907">
                                {admin.phone}
                              </a>
                            </div>}
                        </div>
                      </div>)}
                  </div> : <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500 border border-gray-100" data-id="element-4908">
                    No administrators found. Please contact your IT department.
                  </div>}
              </div>

              <button type="button" onClick={() => {
            setStep('email');
            setError(null);
          }} className={btnClass} data-id="element-4909">
                <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-4910" />
                Try a different email
              </button>
            </div>
          </>}

        <p className="text-center text-xs text-gray-400 mt-8" data-id="element-4911">
          Protected by ConfigConsole Identity
        </p>
      </div>
    </div>;
}